import { useState } from "react";
import axios from "axios";
import { AnimatePresence, motion } from "framer-motion";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";

const PROJECT_TYPES = [
  "Brand Strategy",
  "Brand Identity",
  "Naming",
  "Visual System",
  "Positioning",
  "Growth Advisory",
];

const BUDGETS = [
  "Under $15k",
  "$15k — $30k",
  "$30k — $60k",
  "$60k — $120k",
  "$120k+",
];

const TIMELINES = ["Q1 2026", "Q2 2026", "Q3 2026", "Q4 2026", "Flexible"];

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STEPS = ["Type", "Budget", "Timeline", "Brief", "About you"];

export default function IntakeDialog({ open, onOpenChange }) {
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [form, setForm] = useState({
    project_type: "",
    budget: "",
    timeline: "",
    brief: "",
    name: "",
    email: "",
    company: "",
  });

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const canNext = () => {
    if (step === 0) return !!form.project_type;
    if (step === 1) return !!form.budget;
    if (step === 2) return !!form.timeline;
    if (step === 3) return form.brief.trim().length >= 10;
    if (step === 4) return form.name.trim() && /.+@.+\..+/.test(form.email);
    return false;
  };

  const submit = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/intake`, form);
      setSent(true);
      toast.success("Received. We'll be in touch within 48 hours.");
    } catch (e) {
      const msg = typeof e?.response?.data?.detail === "string"
        ? e.response.data.detail
        : "Something went wrong. Please try again.";
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const reset = () => {
    setStep(0);
    setSent(false);
    setForm({ project_type: "", budget: "", timeline: "", brief: "", name: "", email: "", company: "" });
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        onOpenChange(v);
        if (!v) setTimeout(reset, 300);
      }}
    >
      <DialogContent
        className="max-w-3xl bg-ivory border border-charcoal/20 p-0 rounded-none [&>button]:hidden overflow-hidden"
        data-testid="intake-dialog"
      >
        <div className="grid grid-cols-12">
          <div className="hidden md:flex col-span-4 flex-col justify-between bg-charcoal text-ivory p-8">
            <div>
              <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-ivory/60">Bosecrafts</p>
              <DialogTitle className="mt-8 font-display text-4xl leading-tight tracking-tightest">
                Start a project.
              </DialogTitle>
              <DialogDescription className="mt-3 text-ivory/70 text-sm leading-relaxed">
                We take 6&ndash;8 briefs a year. This helps us know if we're the right studio for yours.
              </DialogDescription>
            </div>
            <div className="mt-8 space-y-3">
              {STEPS.map((s, i) => (
                <div
                  key={s}
                  className={`flex items-center gap-3 text-[11px] uppercase tracking-[0.24em] ${
                    i === step ? "text-ivory" : i < step ? "text-ivory/60" : "text-ivory/30"
                  }`}
                >
                  <span className="font-mono w-6">{String(i + 1).padStart(2, "0")}</span>
                  <span>{s}</span>
                  {i < step && <span className="ml-auto text-ivory/60">✓</span>}
                </div>
              ))}
            </div>
          </div>

          <div className="col-span-12 md:col-span-8 p-8 md:p-10 min-h-[520px] flex flex-col">
            <button
              className="self-end text-[11px] uppercase tracking-[0.25em] text-stone-muted hover:text-charcoal"
              onClick={() => onOpenChange(false)}
              data-testid="intake-close"
            >
              Close ✕
            </button>

            {sent ? (
              <div className="flex-1 flex flex-col justify-center" data-testid="intake-success">
                <p className="font-mono text-[11px] uppercase tracking-[0.24em] text-stone-muted">Received</p>
                <h3 className="font-display text-5xl mt-4 tracking-tightest leading-tight">
                  Thank you, {form.name.split(" ")[0]}.
                </h3>
                <p className="mt-4 text-charcoal/70 leading-relaxed max-w-md">
                  Your brief is with us. Expect a personal reply within 48 hours &mdash; usually with a
                  handful of pointed questions before we consider a call.
                </p>
                <button
                  onClick={() => onOpenChange(false)}
                  className="mt-10 self-start border border-charcoal bg-charcoal text-ivory px-6 py-3 text-[11px] uppercase tracking-[0.22em] hover:bg-ivory hover:text-charcoal transition-colors"
                >
                  Close
                </button>
              </div>
            ) : (
              <>
                <div className="mt-6 flex-1">
                  <AnimatePresence mode="wait">
                    <motion.div
                      key={step}
                      initial={{ opacity: 0, y: 16 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -16 }}
                      transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                    >
                      {step === 0 && (
                        <Step title="What do you need built?" subtitle="Pick the closest — we can refine on a call.">
                          <div className="grid grid-cols-2 gap-3" data-testid="step-project-type">
                            {PROJECT_TYPES.map((t) => (
                              <PickButton
                                key={t}
                                selected={form.project_type === t}
                                onClick={() => update("project_type", t)}
                                testid={`pt-${t.replace(/\s+/g, "-").toLowerCase()}`}
                              >
                                {t}
                              </PickButton>
                            ))}
                          </div>
                        </Step>
                      )}
                      {step === 1 && (
                        <Step title="Budget range." subtitle="Real numbers help us give a real answer.">
                          <div className="grid grid-cols-1 gap-2" data-testid="step-budget">
                            {BUDGETS.map((b) => (
                              <PickButton
                                key={b}
                                selected={form.budget === b}
                                onClick={() => update("budget", b)}
                                testid={`b-${b.replace(/\s+/g, "-").toLowerCase()}`}
                              >
                                {b}
                              </PickButton>
                            ))}
                          </div>
                        </Step>
                      )}
                      {step === 2 && (
                        <Step title="When would you like to start?" subtitle="Our next open slot is Q3 2026.">
                          <div className="grid grid-cols-2 gap-3" data-testid="step-timeline">
                            {TIMELINES.map((t) => (
                              <PickButton
                                key={t}
                                selected={form.timeline === t}
                                onClick={() => update("timeline", t)}
                                testid={`tl-${t.replace(/\s+/g, "-").toLowerCase()}`}
                              >
                                {t}
                              </PickButton>
                            ))}
                          </div>
                        </Step>
                      )}
                      {step === 3 && (
                        <Step title="Tell us about the brand." subtitle="A paragraph is plenty. What's the shape of it?">
                          <textarea
                            value={form.brief}
                            onChange={(e) => update("brief", e.target.value)}
                            rows={7}
                            data-testid="step-brief"
                            placeholder="What are you making, who is it for, and what's currently in the way?"
                            className="w-full bg-transparent border-b border-charcoal/40 focus:border-charcoal outline-none py-3 text-base font-serif resize-none placeholder:text-stone-soft"
                          />
                        </Step>
                      )}
                      {step === 4 && (
                        <Step title="And you are…" subtitle="We keep this to a small circle. No newsletter.">
                          <div className="space-y-6">
                            <Field label="Full name" testid="step-name">
                              <input
                                value={form.name}
                                onChange={(e) => update("name", e.target.value)}
                                placeholder="Jane Founder"
                                className="w-full bg-transparent border-b border-charcoal/40 focus:border-charcoal outline-none py-2 text-base font-serif placeholder:text-stone-soft"
                              />
                            </Field>
                            <Field label="Email" testid="step-email">
                              <input
                                type="email"
                                value={form.email}
                                onChange={(e) => update("email", e.target.value)}
                                placeholder="jane@company.com"
                                className="w-full bg-transparent border-b border-charcoal/40 focus:border-charcoal outline-none py-2 text-base font-serif placeholder:text-stone-soft"
                              />
                            </Field>
                            <Field label="Company (optional)" testid="step-company">
                              <input
                                value={form.company}
                                onChange={(e) => update("company", e.target.value)}
                                placeholder="Northfield & Co."
                                className="w-full bg-transparent border-b border-charcoal/40 focus:border-charcoal outline-none py-2 text-base font-serif placeholder:text-stone-soft"
                              />
                            </Field>
                          </div>
                        </Step>
                      )}
                    </motion.div>
                  </AnimatePresence>
                </div>

                <div className="mt-8 pt-6 border-t border-charcoal/15 flex items-center justify-between">
                  <span className="font-mono text-[11px] uppercase tracking-[0.24em] text-stone-muted">
                    Step {String(step + 1).padStart(2, "0")} / {String(STEPS.length).padStart(2, "0")}
                  </span>
                  <div className="flex items-center gap-3">
                    {step > 0 && (
                      <button
                        onClick={() => setStep((s) => s - 1)}
                        data-testid="intake-back"
                        className="px-4 py-2 text-[11px] uppercase tracking-[0.22em] text-stone-muted hover:text-charcoal"
                      >
                        ← Back
                      </button>
                    )}
                    {step < STEPS.length - 1 ? (
                      <button
                        disabled={!canNext()}
                        onClick={() => setStep((s) => s + 1)}
                        data-testid="intake-next"
                        className="border border-charcoal bg-charcoal text-ivory disabled:bg-stone-soft disabled:border-stone-soft disabled:cursor-not-allowed px-6 py-3 text-[11px] uppercase tracking-[0.22em] hover:bg-ivory hover:text-charcoal transition-colors"
                      >
                        Next →
                      </button>
                    ) : (
                      <button
                        disabled={!canNext() || busy}
                        onClick={submit}
                        data-testid="intake-submit"
                        className="border border-charcoal bg-charcoal text-ivory disabled:bg-stone-soft disabled:border-stone-soft disabled:cursor-not-allowed px-6 py-3 text-[11px] uppercase tracking-[0.22em] hover:bg-ivory hover:text-charcoal transition-colors"
                      >
                        {busy ? "Sending…" : "Send brief ↗"}
                      </button>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Step({ title, subtitle, children }) {
  return (
    <div>
      <h3 className="font-display text-3xl md:text-4xl tracking-tightest leading-tight">{title}</h3>
      <p className="mt-2 text-sm text-stone-muted">{subtitle}</p>
      <div className="mt-8">{children}</div>
    </div>
  );
}

function PickButton({ selected, onClick, children, testid }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className={`text-left px-4 py-3 border text-sm font-serif transition-colors duration-300 ${
        selected
          ? "border-charcoal bg-charcoal text-ivory"
          : "border-charcoal/25 hover:border-charcoal text-charcoal bg-transparent"
      }`}
    >
      {children}
    </button>
  );
}

function Field({ label, children, testid }) {
  return (
    <label className="block" data-testid={testid}>
      <span className="block font-mono text-[10px] uppercase tracking-[0.24em] text-stone-muted mb-2">
        {label}
      </span>
      {children}
    </label>
  );
}
